import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import newsAPI from '../../api/newsAPI';
import getAsyncErrorMessage from '../../utils/getAsyncErrorMessage';
import { GENERAL_ERROR_MESSAGE } from '../../constants';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AppThunk } from '../store';
import type { RejectedWithValueAction } from '../../types/common';
import type { CreateNewsParams, DeleteNewsParams, NewsState, ModalMode, NewsType, UpdateNewsParamsClient } from '../../types/news';

export const initialState: NewsState = {
  isLoading: false,
  hasError: false,
  serverMessage: undefined,
  dialogMode: undefined,
  isDialogOpen: false,
  news: undefined,
  newss: undefined,
  newssForManage: undefined,
};

const slice = createSlice({
  name: 'News',
  initialState,
  reducers: {
    resetServerMessage(state: NewsState) {
      state.isLoading = false;
      state.serverMessage = '';
      state.hasError = false;
    },

    setServerMessage(state: NewsState, action: PayloadAction<string>): void {
      state.isLoading = false;
      state.serverMessage = action.payload;
    },

    setDialogMode(state: NewsState, action: PayloadAction<string>): void {
      state.dialogMode = action.payload;
    },

    openDialog(state: NewsState): void {
      state.isDialogOpen = true;
    },

    closeDialog(state: NewsState): void {
      state.isDialogOpen = false;
      state.dialogMode = undefined;
      state.news = undefined;
    },

    selectNews(state: NewsState, action: PayloadAction<NewsType>): void {
      if (action.payload !== undefined) {
        const news = action.payload;
        const selectedNews: NewsType | undefined = state.newssForManage.find((org) => org.id === news.id);
        if (selectedNews !== undefined) {
          state.news = selectedNews;
        } else {
          state.news = action.payload;
        }
      } else {
        state.news = action.payload;
      }
    },
  },

  extraReducers: (builder) => {
    builder
      // Get Newss
      // ----------------------------------------
      .addCase(getNewss.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getNewss.fulfilled, (state, action) => {
        state.isLoading = false;
        state.newss = action.payload;
      })
      .addCase(getNewss.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<undefined, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
        state.newss = state.newss === undefined ? [] : state.newss;
      })
      // Get Newss for Manage
      // ----------------------------------------
      .addCase(getNewssForManage.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getNewssForManage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.newssForManage = action.payload;
      })
      .addCase(getNewssForManage.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<undefined, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
        state.newssForManage = state.newssForManage === undefined ? [] : state.newssForManage;
      })
      // Create Newss
      // ----------------------------------------
      .addCase(createNews.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createNews.fulfilled, (state, action) => {
        state.isLoading = false;
        state.newssForManage = state.newssForManage === undefined ? [action.payload] : [...state.newssForManage, action.payload];
      })
      .addCase(createNews.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<CreateNewsParams, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
        state.newssForManage = state.newssForManage === undefined ? [] : state.newssForManage;
      })
      // Update Newss
      // ----------------------------------------
      .addCase(updateNews.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateNews.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedNews = action.payload;
        state.newssForManage = state.newssForManage.map((news) => {
          if (news.id === updatedNews.id) {
            return updatedNews;
          } else {
            return news;
          }
        });
      })
      .addCase(updateNews.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<UpdateNewsParamsClient, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
        state.newssForManage = state.newssForManage === undefined ? [] : state.newssForManage;
      })
      // Delete Newss
      // ----------------------------------------
      .addCase(deleteNews.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteNews.fulfilled, (state, action) => {
        state.isLoading = false;
        const deletedNews = action.payload;
        state.news = undefined;
        state.newssForManage = state.newssForManage.filter((news) => {
          if (news.id === deletedNews.id) {
            return false;
          } else {
            return true;
          }
        });
      })
      .addCase(deleteNews.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<DeleteNewsParams, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
        state.news = undefined;
        state.newssForManage = state.newssForManage === undefined ? [] : state.newssForManage;
      });
  },
});

export const { reducer, actions } = slice;

export const resetServerMessage =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.resetServerMessage());
  };

export const setDialogMode =
  (mode?: ModalMode): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.setDialogMode(mode));
  };

export const openDialog =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.openDialog());
  };

export const closeDialog =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.closeDialog());
  };

export const selectNews =
  (news?: NewsType): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.selectNews(news));
  };

export const getNewss = createAsyncThunk('News/getNewss', async (_, thunkAPI) => {
  try {
    const response = await newsAPI.getNewss();
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const getNewssForManage = createAsyncThunk('News/getNewssForManage', async (_, thunkAPI) => {
  try {
    const response = await newsAPI.getNewssForManage();
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const createNews = createAsyncThunk('News/createNews', async (params: CreateNewsParams, thunkAPI) => {
  try {
    const response = await newsAPI.createNews(params);
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const updateNews = createAsyncThunk('News/updateNews', async (params: UpdateNewsParamsClient, thunkAPI) => {
  try {
    const response = await newsAPI.updateNews(params);
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const deleteNews = createAsyncThunk('News/deleteNews', async (params: DeleteNewsParams, thunkAPI) => {
  try {
    const response = await newsAPI.deleteNews(params);
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export default slice;
